import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine, Legend
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { Holding, computeTotals, Lot } from '../../services/holdingsService';
import { cn } from '../../lib/utils';
import { safeToFixed } from '../../lib/formatters';

interface PerformanceChartProps {
  holdings: Holding[];
  prices: Record<string, number>;
}

const SP500_WINDOWS: Record<string, number> = { '1Y': 1, '3Y': 3, '5Y': 5, '10Y': 10 };

export function PerformanceChart({ holdings, prices }: PerformanceChartProps) {
  const [windowPeriod, setWindowPeriod] = useState<'1Y' | '3Y' | '5Y' | '10Y'>('1Y');
  const [spReturn, setSpReturn] = useState<number | null>(null);
  const [spLoading, setSpLoading] = useState(true);
  const [spyHistory, setSpyHistory] = useState<{ date: string; close: number }[]>([]);

  // ── Mode detection ───────────────────────────────────────────────────────
  // Time-series mode requires EVERY holding to have EVERY buy lot dated
  // before yesterday. If even one holding or one lot is missing a real date,
  // fall back to snapshot mode — partial data produces misleading charts.
  const hasRealDates = useMemo(() => {
    if (holdings.length === 0) return false;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return holdings.every(h => {
      const buyLots = (h.lots ?? []).filter(
        l => l.type === 'buy' || (!(l as any).type && !l.dateSold)
      );
      if (buyLots.length === 0) return false;
      return buyLots.every(l => new Date(l.datePurchased) < yesterday);
    });
  }, [holdings]);

  // ── Earliest buy date (for time-series mode) ─────────────────────────────
  const earliestDate = useMemo(() => {
    if (!hasRealDates) return null;
    let earliest = new Date();
    holdings.forEach(h => {
      (h.lots ?? []).forEach(l => {
        if (l.type === 'buy' || (!(l as any).type && !l.dateSold)) {
          const d = new Date(l.datePurchased);
          if (d < earliest) earliest = d;
        }
      });
    });
    return earliest;
  }, [holdings, hasRealDates]);

  // ── SPY data fetch ────────────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;
    const fetchSpy = async () => {
      setSpLoading(true);
      try {
        let fromStr: string;
        let toStr: string = new Date().toISOString().split('T')[0];

        if (hasRealDates && earliestDate) {
          fromStr = earliestDate.toISOString().split('T')[0];
        } else {
          const fromDate = new Date();
          fromDate.setFullYear(fromDate.getFullYear() - SP500_WINDOWS[windowPeriod]);
          fromStr = fromDate.toISOString().split('T')[0];
        }

        const res = await fetch(
          `/api/fmp/stable/historical-price-eod/full?symbol=SPY&from=${fromStr}&to=${toStr}`
        );
        if (!res.ok) throw new Error('SPY fetch failed');
        const raw = await res.json();
        if (!isMounted) return;

        // FMP stable/historical-price-eod/full returns { historical: [...] }
        const data: any[] = raw.historical ?? raw;
        if (!data || !data.length) return;

        // data is sorted newest-first — reverse for chronological order
        const sorted = [...data].reverse();

        if (hasRealDates) {
          setSpyHistory(sorted.map((d: any) => ({ date: d.date, close: d.close })));
        } else {
          const newest = data[0].close;
          const oldest = data[data.length - 1].close;
          setSpReturn(((newest - oldest) / oldest) * 100);
        }
      } catch (err) {
        console.error('SPY fetch error:', err);
      } finally {
        if (isMounted) setSpLoading(false);
      }
    };
    fetchSpy();
    return () => { isMounted = false; };
  }, [windowPeriod, hasRealDates, earliestDate]);

  // ── Snapshot mode: aggregate portfolio return ─────────────────────────────
  const portfolioMetrics = useMemo(() => {
    if (hasRealDates) return null;
    let totalCost = 0;
    let totalValue = 0;
    let counted = 0;
    holdings.forEach(h => {
      const price = prices[h.ticker];
      const { totalShares, avgCostPerShare } = computeTotals(h.lots ?? []);
      if (!price || !avgCostPerShare || !totalShares) return;
      totalCost += avgCostPerShare * totalShares;
      totalValue += price * totalShares;
      counted++;
    });
    if (counted === 0 || totalCost === 0) return null;
    return {
      returnPct: parseFloat((((totalValue - totalCost) / totalCost) * 100).toFixed(1)),
      totalCost,
      totalValue,
      counted,
    };
  }, [holdings, prices, hasRealDates]);

  // ── Time-series mode: monthly indexed chart data ──────────────────────────
  const timeSeriesData = useMemo(() => {
    if (!hasRealDates || !earliestDate || spyHistory.length === 0) return [];

    // Collect all buy lots across all holdings sorted by date
    const allBuys: { date: Date; totalCostPaid: number }[] = [];
    holdings.forEach(h => {
      (h.lots ?? [])
        .filter(l => l.type === 'buy' || (!(l as any).type && !l.dateSold))
        .forEach(l => allBuys.push({
          date: new Date(l.datePurchased),
          totalCostPaid: l.totalCostPaid,
        }));
    });

    const firstMonthEnd = new Date(earliestDate.getFullYear(), earliestDate.getMonth() + 1, 0);
    const firstMonthCost = allBuys
      .filter(b => b.date <= firstMonthEnd)
      .reduce((sum, b) => sum + b.totalCostPaid, 0);

    if (firstMonthCost === 0) return [];

    // Helper: find nearest SPY close on or before a given date
    const getNearestSpyClose = (targetDate: Date): number | null => {
      for (let i = spyHistory.length - 1; i >= 0; i--) {
        if (new Date(spyHistory[i].date) <= targetDate) {
          return spyHistory[i].close;
        }
      }
      return null;
    };

    // Precompute hypothetical SPY shares per lot
    const spyLotShares: { date: Date; spyShares: number }[] = allBuys.map(b => {
      const spyPriceAtPurchase = getNearestSpyClose(b.date);
      if (!spyPriceAtPurchase || spyPriceAtPurchase === 0) return { date: b.date, spyShares: 0 };
      return {
        date: b.date,
        spyShares: b.totalCostPaid / spyPriceAtPurchase,
      };
    });

    // Build one data point per month from earliestDate to today
    const points: { month: string; portfolioIndex: number; spyIndex: number }[] = [];
    const cursor = new Date(earliestDate);
    cursor.setDate(1); // start of month
    const now = new Date();

    while (cursor <= now) {
      const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);

      // Cumulative cost invested up to this month
      const cumulativeCost = allBuys
        .filter(b => b.date <= monthEnd)
        .reduce((sum, b) => sum + b.totalCostPaid, 0);

      const hypotheticalSpyShares = spyLotShares
        .filter(s => s.date <= monthEnd)
        .reduce((sum, s) => sum + s.spyShares, 0);

      const spyCloseAtMonthEnd = getNearestSpyClose(monthEnd);
      const hypotheticalSpyValue = spyCloseAtMonthEnd !== null
        ? hypotheticalSpyShares * spyCloseAtMonthEnd
        : 0;

      const spyIndex = firstMonthCost > 0 && hypotheticalSpyValue > 0
        ? parseFloat(((hypotheticalSpyValue / firstMonthCost) * 100).toFixed(1))
        : null;

      if (cumulativeCost > 0 && spyIndex !== null) {
        points.push({
          month: cursor.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          portfolioIndex: firstMonthCost > 0
            ? parseFloat(((cumulativeCost / firstMonthCost) * 100).toFixed(1))
            : 100,
          spyIndex: spyIndex,
        });
      }

      cursor.setMonth(cursor.getMonth() + 1);
    }

    return points;
  }, [holdings, hasRealDates, earliestDate, spyHistory]);

  // ── Snapshot chart data ───────────────────────────────────────────────────
  const snapshotData = useMemo(() => {
    const d = [];
    if (portfolioMetrics) d.push({ name: 'My Portfolio', gain: portfolioMetrics.returnPct });
    if (spReturn !== null) d.push({ name: 'S&P 500', gain: parseFloat(spReturn.toFixed(1)) });
    return d;
  }, [portfolioMetrics, spReturn]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (spLoading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-ink-500" />
      </div>
    );
  }

  // ── Time-series chart ─────────────────────────────────────────────────────
  if (hasRealDates) {
    if (timeSeriesData.length < 2) {
      return (
        <p className="text-sm text-ink-500 text-center py-8">
          Not enough historical data to render chart.
        </p>
      );
    }
    return (
      <div className="w-full">
        <div className="text-xs text-ink-500 mb-4">
          Since {earliestDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>
        <ResponsiveContainer width="100%" height={280} minWidth={1} minHeight={1}>
          <LineChart data={timeSeriesData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: '#78716c' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#78716c' }}
              tickFormatter={v => `${v}`}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                safeToFixed(value, 1),
                name === 'portfolioIndex' ? 'My Portfolio' : 'S&P 500 (SPY)',
              ]}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
              labelStyle={{ fontSize: '10px', fontWeight: 'bold', color: '#57534e' }}
              itemStyle={{ fontSize: '12px', fontWeight: 'bold' }}
            />
            <Legend
              formatter={value => value === 'portfolioIndex' ? 'My Portfolio' : 'S&P 500 (SPY)'}
            />
            <Line
              type="monotone"
              dataKey="portfolioIndex"
              stroke="#6E1D24"
              strokeWidth={2}
              dot={false}
              name="portfolioIndex"
            />
            <Line
              type="monotone"
              dataKey="spyIndex"
              stroke="#78716c"
              strokeWidth={2}
              dot={false}
              name="spyIndex"
            />
          </LineChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-ink-500 mt-2 text-center">
          Indexed to 100 at first purchase date. Portfolio line reflects cumulative cost invested. S&P 500 (SPY) shows equivalent DCA into SPY on the same dates and amounts.
        </p>
      </div>
    );
  }

  // ── Snapshot chart ────────────────────────────────────────────────────────
  return (
    <div className="w-full">
      {/* Window selector — only shown in snapshot mode */}
      <div className="flex justify-center md:justify-start gap-2 mb-6">
        {(['1Y', '3Y', '5Y', '10Y'] as const).map(opt => (
          <button
            key={opt}
            onClick={() => setWindowPeriod(opt)}
            className={cn(
              "text-xs font-medium uppercase tracking-wider px-3 py-1 rounded-md transition-colors",
              windowPeriod === opt
                ? "bg-oxblood text-paper"
                : "bg-surface-2 text-ink-500 hover:bg-surface"
            )}
          >
            {opt}
          </button>
        ))}
      </div>

      {snapshotData.length === 0 ? (
        <p className="text-sm text-ink-500 text-center py-8">
          Add cost basis to your positions to see performance comparison.
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={160} minWidth={1} minHeight={1}>
            <BarChart
              data={snapshotData}
              layout="vertical"
              margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={v => `${v > 0 ? '+' : ''}${v}%`}
                tick={{ fontSize: 12, fill: '#78716c' }}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={90}
                tick={{ fontSize: 12, fill: '#57534e', fontWeight: 500 }}
              />
              <Tooltip
                formatter={(value: number) => [`${value > 0 ? '+' : ''}${value}%`, 'Return']}
                cursor={{ fill: '#F0EADF' }}
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                labelStyle={{ fontSize: '10px', fontWeight: 'bold', color: '#57534e' }}
                itemStyle={{ fontSize: '12px', fontWeight: 'bold' }}
              />
              <ReferenceLine x={0} stroke="#d6d3d1" />
              <Bar dataKey="gain" radius={[0, 4, 4, 0]} barSize={40}>
                {snapshotData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.name === 'S&P 500' ? '#78716c' : '#6E1D24'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {portfolioMetrics && (
            <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-ink-200 text-xs text-ink-500">
              <div>
                <p className={cn("font-medium text-sm", portfolioMetrics.returnPct >= 0 ? "text-success-600" : "text-danger-600")}>
                  {portfolioMetrics.returnPct >= 0 ? '+' : ''}{portfolioMetrics.returnPct}%
                </p>
                <p>Portfolio return</p>
              </div>
              <div>
                <p className="font-medium text-ink text-sm">${safeToFixed(portfolioMetrics.totalValue, 0)}</p>
                <p>Est. current value</p>
              </div>
              <div>
                <p className="font-medium text-ink text-sm">${safeToFixed(portfolioMetrics.totalCost, 0)}</p>
                <p>Total cost basis</p>
              </div>
              <div>
                <p className="font-medium text-ink text-sm">{portfolioMetrics.counted}</p>
                <p>Positions tracked</p>
              </div>
            </div>
          )}
        </>
      )}

      <p className="text-[10px] text-ink-500 mt-3 text-center">
        Portfolio return calculated from average cost basis vs current price. S&P 500 (SPY) shown for selected window. Add transaction dates to all positions to unlock time-series view.
      </p>
    </div>
  );
}
